// Pure, DOM-free view model for the Tasks tab toolbar: LAYOUT
// (board|list|calendar|spreadsheet|gantt), GROUP BY (status|assignee|priority),
// and FILTERS (assignee ids, assignee
// KINDS, priorities, state groups). All grouping + filtering lives here as pure,
// deterministic functions so the board / list components are a thin render over
// the result, and so "per user / per agent / overall" is unit-testable without
// mounting a component.
//
// The toolbar maps onto the user's three asks like this:
//   - "Overall"      → no filters active (the default); every task shows.
//   - "per user"     → GROUP BY assignee gives a section per identity, and the
//                      assignee KIND facet ("People") narrows to humans.
//   - "per agent"    → same group-by, with the KIND facet set to "Agents"
//                      (and/or "Cybos").
import type { Task, TaskState } from "$lib/core/types.js";
import { stateGroupOfTask, type ColumnKey } from "./board.js";
import { STATE_GROUPS, type StateGroupKey } from "./constants.js";
import { priorityForTask, type Priority, PRIORITY_ORDER } from "./priority.js";
import { resolveAssignee, type AssigneeKind, type AssigneePools } from "./assignee.js";
import {
  UNKNOWN_CYCLE_LABEL,
  UNKNOWN_LABEL_LABEL,
  UNKNOWN_MODULE_LABEL,
  UNKNOWN_PROJECT_LABEL,
} from "$lib/unknown-label.js";

// The minimum a board column / list group needs off a project's workflow state to
// render data-driven columns: the state id (the task.stateId key + the drop
// target written back via client.updateTask), the human name, the editable color,
// and the canonical Plane phase `group` (drives the StateGroupIcon glyph). Mirror
// of the relevant TaskState fields, so callers can pass either a full TaskState[]
// (from client.fetchProjectStates) or a lighter projection. Ordered by the
// caller (the relay returns states by `sequence`), so grouping preserves input
// order — it does NOT re-sort.
export type StateLite = Pick<TaskState, "id" | "name" | "color" | "group">;

// Plane offers five issue layouts. Board + List are the two original Tasks
// surfaces; calendar / spreadsheet / gantt are the new Plane-faithful layouts.
// Grouping / sorting / filtering below stay layout-agnostic — they operate on
// the Task[] regardless of which layout renders the result, so adding a layout
// value here changes nothing about how tasks are grouped, filtered, or sorted.
// (Calendar + Gantt position tasks by their own date math and may ignore the
// active GROUP BY entirely; they still accept it for prop-shape parity.)
export type Layout = "board" | "list" | "calendar" | "spreadsheet" | "gantt";
// GROUP BY facets. assignee / priority / stateGroup are the primary three;
// project, label, cycle, module, createdBy, and none are the Plane-faithful
// additions. The extra facets read their value FORWARD-COMPATIBLY off the Task,
// exactly like priority.ts narrows an optional `priority`. A task with no
// project/labels/cycle/module groups into the "none" catch-all bucket rather than
// being dropped, so adding a facet never hides a task. `createdBy` groups by the
// task's creator (resolved through the assignee pools); `none` collapses grouping
// entirely into a single flat list (Plane's "no grouping" mode).
//
// The legacy "status" facet (the four-column todo/in_progress/pending_review/
// done vocabulary, board.ts) is RETIRED — removed, not disabled, per the "State
// everywhere" product decision. "stateGroup" (groupByStateGroup, below) is the
// sole state-grouping facet: it always renders the five REAL Plane state groups
// (Backlog/To Do/In Progress/Done/Cancelled), never folding Backlog or Cancelled
// into an adjacent column the way the legacy fixed columns did.
export type GroupBy =
  | "assignee"
  | "priority"
  | "project"
  | "label"
  | "cycle"
  | "module"
  | "stateGroup"
  | "createdBy"
  | "none";

// A task MAY (forward-compatibly) carry project / label / cycle / module
// associations. Narrowed here without widening the canonical Task type or
// asserting `any` — mirrors priority.ts's MaybePrioritized pattern. `labels` and
// `moduleIds` are id ARRAYS (a task can carry many labels AND belong to many
// modules — see core/types.ts `moduleIds`, relay `module_ids`); project/cycle are
// single optional ids. `moduleId` (singular) is tolerated only as a legacy alias.
interface MaybeAssociated {
  projectId?: string | null;
  cycleId?: string | null;
  moduleIds?: string[] | null;
  moduleId?: string | null;
  labels?: string[] | null;
  labelIds?: string[] | null;
}

// Read a task's project / cycle id (or null when absent/unset).
function projectOf(task: Task): string | null {
  return (task as MaybeAssociated).projectId ?? null;
}
function cycleOf(task: Task): string | null {
  return (task as MaybeAssociated).cycleId ?? null;
}
// Read a task's module ids. Modules are MANY-per-task (like labels): the canonical
// field is `moduleIds` (plural array). A legacy `moduleId` singular is tolerated
// and lifted into a one-element array so older rows still group/filter. Empty [].
function modulesOf(task: Task): string[] {
  const m = task as MaybeAssociated;
  if (m.moduleIds && m.moduleIds.length > 0) return m.moduleIds;
  return m.moduleId ? [m.moduleId] : [];
}
// Read a task's label ids (tolerating either `labels` or `labelIds`), or [].
function labelsOf(task: Task): string[] {
  const m = task as MaybeAssociated;
  return m.labels ?? m.labelIds ?? [];
}
// True when a task carries a bound schedule — the recurring/scheduled signal the
// "Recurring" filter facet keys off. `schedule` is a canonical Task field
// (core/types.ts), denormalized read-only by the relay; null/absent when no
// schedule is bound (or an older relay omits it). Read defensively so a row that
// predates the field reads as non-recurring rather than throwing.
function isRecurring(task: Task): boolean {
  return (task.schedule ?? null) != null;
}
// The task's REAL Plane phase. Resolves `task.stateId` through the project's
// states catalog FIRST (StateLite carries the canonical `group`): the catalog is
// local, so an optimistic drag that has already written the new stateId reads as
// moved a beat before the relay echoes the new group back. Otherwise the group
// the relay sent on the task itself (stateGroupOfTask, board.ts) — which is what
// a caller with no catalog at all (a cross-project view, an Overview widget) has
// to go on.
function stateGroupOf(task: Task, statesById?: Map<string, StateGroupKey>): StateGroupKey {
  if (statesById && task.stateId) {
    const phase = statesById.get(task.stateId);
    if (phase) return phase;
  }
  return stateGroupOfTask(task);
}

// Build a stateId → phase lookup from a states catalog (StateLite[]), for the
// state-group grouping/filtering above. Empty/absent catalog → an empty map, so
// stateGroupOf transparently falls back to the group carried on the task.
function statePhaseIndex(states: StateLite[]): Map<string, StateGroupKey> {
  const m = new Map<string, StateGroupKey>();
  for (const s of states) if (s.group) m.set(s.id, s.group);
  return m;
}

const DAY_MS = 86_400_000;

// The board's Done column defaults to a TIME WINDOW (Today / Last 7 days / Last
// 30 days / a custom range / All), applied ONLY to tasks whose real state group
// is "completed" — every other column is never touched by this facet. "all" is
// the escape hatch (the old "show older" affordance now maps here): no filtering
// at all. `custom` bounds are epoch ms, inclusive on both ends (mirrors the
// due/start-date range convention in WorkItemFiltersRow).
export type DoneWindow =
  | { kind: "today" }
  | { kind: "last7" }
  | { kind: "last30" }
  | { kind: "custom"; from: number; to: number }
  | { kind: "all" };

// Last 7 days, so yesterday's finished work stays on the board overnight; the Done
// column's "Show N older" row widens it.
export const DEFAULT_DONE_WINDOW: DoneWindow = { kind: "last7" };

// Local-calendar-day boundaries of `ms` (Date's local getters — same convention
// WorkItemFiltersRow's startOfDay/endOfDay use for date-range inputs).
function startOfLocalDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}
function endOfLocalDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

// View-layer TIME-WINDOW filter for the board's Done column, SCOPED to the
// completed phase: a completed task is kept only if its REAL completedAt falls
// inside `window` (relative to `now`). A completed task with NO completedAt
// (historical/un-backfilled) is treated as "outside every window" and hidden —
// still reachable via `{ kind: "all" }`. Non-completed tasks, and tasks with no
// resolvable phase, pass through UNTOUCHED regardless of `window` — this is a
// hard product requirement, not a style preference. The states catalog resolves
// each task's true phase (task.stateId → group), falling back to the legacy
// status mapping when absent (mirrors stateGroupOf), so a completed card is
// never misclassified as non-completed and silently kept.
export function filterDoneByWindow(
  tasks: Task[],
  states: StateLite[],
  window: DoneWindow,
  now: number,
): Task[] {
  if (window.kind === "all") return tasks;
  const phaseIndex = states.length > 0 ? statePhaseIndex(states) : undefined;
  let from: number;
  let to: number | null = null;
  switch (window.kind) {
    case "today":
      from = startOfLocalDay(now);
      to = endOfLocalDay(now);
      break;
    case "last7":
      from = now - 7 * DAY_MS;
      break;
    case "last30":
      from = now - 30 * DAY_MS;
      break;
    case "custom":
      from = window.from;
      to = window.to;
      break;
  }
  return tasks.filter((t) => {
    if (stateGroupOf(t, phaseIndex) !== "completed") return true;
    if (t.completedAt == null || t.completedAt < from) return false;
    if (to != null && t.completedAt > to) return false;
    return true;
  });
}

// Human label for the window control's trigger (e.g. "Today", "Last 7 days").
// "custom" reads generically here — the trigger renders the picked dates itself
// via the from/to inputs, this is only the fallback/menu-option label.
export function doneWindowLabel(window: DoneWindow): string {
  switch (window.kind) {
    case "today":
      return "Today";
    case "last7":
      return "Last 7 days";
    case "last30":
      return "Last 30 days";
    case "custom":
      return "Custom";
    case "all":
      return "All time";
  }
}

// The Done column's empty-state copy for the active window — parameterizes the
// generic "No tasks" text so an empty column tells you WHY (product Rule 0: a
// void is a defect). "all" has no active constraint, so it falls back to the
// generic message (a truly empty completed phase, not a window artifact).
export function doneWindowEmptyLabel(window: DoneWindow): string {
  switch (window.kind) {
    case "today":
      return "No tasks completed today";
    case "last7":
      return "Nothing finished in the last 7 days";
    case "last30":
      return "No tasks completed in the last 30 days";
    case "custom":
      return "No tasks completed in the selected range";
    case "all":
      return "No tasks";
  }
}

// ── ORDER BY ───────────────────────────────────────────────────────────────
// Sort fields the toolbar's "Ordering" menu exposes. EVERY value here maps to a
// real field that exists on the Task type (core/types.ts) — we do NOT invent a
// sort key Plane shows but our data can't honor:
//   - "title"     → Task.title           (lexical A→Z)
//   - "createdAt" → Task.createdAt        (epoch ms)
//   - "updatedAt" → Task.updatedAt        (epoch ms)
//   - "dueAt"     → Task.dueAt            (epoch ms; null = "no due date")
//   - "priority"  → priorityForTask(task) (urgent→low→none via PRIORITY_ORDER)
//   - "sortOrder" → Task.sortOrder        (fractional manual rank; null = unranked)
// Plane's DEFAULT ordering is MANUAL (the drag-to-reorder `sort_order`, ascending),
// so DEFAULT_ORDER_BY is "sortOrder" + ascending — an unranked task (null
// sortOrder) sorts LAST, with createdAt as the deterministic tiebreak.
export type OrderBy = "title" | "createdAt" | "updatedAt" | "dueAt" | "priority" | "sortOrder";
export type OrderDir = "asc" | "desc";

export const DEFAULT_ORDER_BY: OrderBy = "sortOrder";
export const DEFAULT_ORDER_DIR: OrderDir = "asc";

// Rank for the "priority" sort: urgent (highest) → none (lowest). Built off the
// canonical PRIORITY_ORDER so it never drifts from the board/group ordering.
const PRIORITY_RANK: Record<Priority, number> = (() => {
  const order: Priority[] = [...PRIORITY_ORDER, "none"];
  const rank = {} as Record<Priority, number>;
  // Higher number = higher priority, so "asc" puts low first and "desc" puts
  // urgent first — matching Plane (descending priority = urgent at the top).
  order.forEach((p, i) => {
    rank[p] = order.length - i;
  });
  return rank;
})();

// Stable comparator for one OrderBy field, ascending. Nulls (no due date) sort
// LAST in ascending order (Plane keeps undated issues at the bottom). Ties keep
// input order via a createdAt then id fallback so the sort is deterministic.
function compareBy(a: Task, b: Task, orderBy: OrderBy): number {
  switch (orderBy) {
    case "title":
      return a.title.localeCompare(b.title);
    case "priority":
      return PRIORITY_RANK[priorityForTask(a)] - PRIORITY_RANK[priorityForTask(b)];
    case "dueAt": {
      // null = no due date → always sort after any dated task in ascending order.
      const av = a.dueAt;
      const bv = b.dueAt;
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return av - bv;
    }
    case "sortOrder": {
      // Manual rank (Task.sortOrder, fractional). null/undefined = unranked →
      // always sort after any ranked task in ascending order (matches the mobile
      // board's nulls-last local sort). The nulls-last-in-BOTH-directions guard in
      // sortTasks keeps unranked rows at the bottom even when dir is "desc".
      const av = a.sortOrder ?? null;
      const bv = b.sortOrder ?? null;
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return av - bv;
    }
    case "updatedAt":
      return a.updatedAt - b.updatedAt;
    default:
      return a.createdAt - b.createdAt;
  }
}

// Sort a COPY of `tasks` by `orderBy`/`dir`. Pure (never mutates the input) and
// deterministic: equal primary keys fall back to createdAt then id so the order
// is stable regardless of the engine's sort stability. The null-due rule is
// applied on the ascending comparator and the whole result is reversed for
// "desc" — except null-due rows, which we keep LAST in both directions to match
// Plane (undated issues never float to the top of a descending due sort).
export function sortTasks(tasks: Task[], orderBy: OrderBy, dir: OrderDir = "asc"): Task[] {
  const sign = dir === "desc" ? -1 : 1;
  return [...tasks].sort((a, b) => {
    // Keep null-due tasks last in BOTH directions (don't let "desc" flip them up).
    if (orderBy === "dueAt") {
      const an = a.dueAt === null;
      const bn = b.dueAt === null;
      if (an !== bn) return an ? 1 : -1;
    }
    // Same nulls-last rule for the manual rank: an unranked task (null sortOrder)
    // stays at the bottom regardless of asc/desc.
    if (orderBy === "sortOrder") {
      const an = (a.sortOrder ?? null) === null;
      const bn = (b.sortOrder ?? null) === null;
      if (an !== bn) return an ? 1 : -1;
    }
    const primary = compareBy(a, b, orderBy);
    if (primary !== 0) return sign * primary;
    // Deterministic tie-break: newest-created first, then id.
    const byCreated = a.createdAt - b.createdAt;
    if (byCreated !== 0) return sign * byCreated;
    return sign * a.id.localeCompare(b.id);
  });
}

// ── DISPLAY PROPERTIES ───────────────────────────────────────────────────────
// Which task properties a card / list row renders. Plane's "Display" menu has a
// "Display properties" section that toggles these chips on the card. Each key
// maps to a real Task field (or a derived value) we already render:
//   - "status"   → the status pill / board column membership
//   - "priority" → the priority dot (priorityForTask)
//   - "assignee" → the assignee avatar (resolveAssignee)
//   - "dueAt"    → the due chip (formatDue / dueChipClass)
//   - "scheduled"→ the per-task schedule cadence chip (cronToLabel), shown only
//                  when the task carries a bound schedule (default-hidden when not)
//   - "taskId"   → the short id chip on the card/row
//   - "labels"   → the per-label color chips (task.labelIds, resolved via catalog)
//   - "startDate"→ the start-date chip (Task.startDate; distinct from dueAt)
//   - "cycle"    → the cycle chip (Task.cycleId, resolved via catalog)
//   - "module"   → the module chip(s) (Task.moduleIds, resolved via catalog)
// "Created by" is deliberately NOT a card display property — Plane keeps the
// creator in the DETAIL panel, not on the card/row (grouping by creator still
// exists via the "createdBy" GroupBy). We do NOT add a display toggle for a
// property the card can't render (estimate /
// sub-issue / attachment / link have no data on our Task, so they get no toggle).
export type DisplayKey =
  | "status"
  | "priority"
  | "assignee"
  | "dueAt"
  | "scheduled"
  | "taskId"
  | "labels"
  | "startDate"
  | "cycle"
  | "module"
  // Not a card CHIP like the others: this one decides whether a parent's
  // sub-work-items render INSIDE its card (on) or keep cards of their own (off).
  // It earns a toggle because folding them removes rows from the board, and a
  // person who wants to drag one child between columns needs a way back.
  | "subItems";

// Display keys default ON — Plane shows every property by default and the user
// opts OUT — except Status (the column already says it) and the Key (noise on a
// card); both stay in Display's Show. A record (not a Set) so it persists cleanly
// as JSON and reads reactively from the preferences store.
export const DEFAULT_DISPLAY: Record<DisplayKey, boolean> = {
  status: false,
  priority: true,
  assignee: true,
  dueAt: true,
  scheduled: true,
  taskId: false,
  labels: true,
  startDate: true,
  cycle: true,
  module: true,
  subItems: true,
};

// The display keys in the order the "Display properties" menu lists them. Pairs
// each key with its human label so the menu never hardcodes the labels.
export const DISPLAY_OPTIONS: { key: DisplayKey; label: string }[] = [
  { key: "status", label: "Status" },
  { key: "priority", label: "Priority" },
  { key: "assignee", label: "Assignee" },
  { key: "dueAt", label: "Due date" },
  { key: "startDate", label: "Start date" },
  { key: "scheduled", label: "Schedule" },
  { key: "labels", label: "Labels" },
  { key: "cycle", label: "Cycle" },
  { key: "module", label: "Module" },
  { key: "taskId", label: "ID" },
  { key: "subItems", label: "Sub-work-items in card" },
];

// Is property `key` currently displayed? Tolerates a partial/absent record
// (older persisted prefs that predate a new key) by defaulting to the
// DEFAULT_DISPLAY value for that key, so a new property shows by default.
export function isDisplayed(prefs: Partial<Record<DisplayKey, boolean>>, key: DisplayKey): boolean {
  return prefs?.[key] ?? DEFAULT_DISPLAY[key];
}

// The active filter set. Empty arrays everywhere = "Overall" (no filtering).
//  - assigneeIds: specific identities (member/cybo/agent ids) to keep.
//  - kinds: the People/Cybos/Agents facet — keep tasks whose assignee resolves
//    to one of these kinds ("user" = People). Unassigned tasks never match a
//    kind filter (nobody is assigned), so a kind filter implicitly hides them.
//  - priorities: keep tasks in these priority buckets.
export interface TaskFilters {
  assigneeIds: string[];
  kinds: AssigneeKind[];
  priorities: Priority[];
  // Plane-faithful additions. All OPTIONAL so an older persisted filter (or a
  // call site that only sets the original three facets) deserializes cleanly —
  // every reader below treats an absent facet as "no selection". projects /
  // cycles / modules / labels keep specific association ids; stateGroups keeps
  // state-group keys — the SOLE status/state filter facet (the legacy 4-value
  // "Status" filter over board.ts's retired ColumnKey vocabulary is gone; it
  // duplicated this facet and was missing Backlog/Cancelled).
  projects?: string[];
  labels?: string[];
  cycles?: string[];
  modules?: string[];
  stateGroups?: StateGroupKey[];
  // WORKFLOW-STATE facet: specific project state ids (task.stateId). Distinct from
  // `stateGroups` (the five coarse phases) — this pins the exact workflow state.
  // Empty/absent = no constraint.
  states?: string[];
  // CREATED-BY facet: identities (member/cybo/agent ids) to keep by task CREATOR
  // (task.createdBy, resolved via the same pools as the assignee facet). Empty/
  // absent = no constraint.
  createdByIds?: string[];
  // DATE-RANGE facets (epoch ms, inclusive bounds; either end optional). `dueFrom`
  // /`dueTo` bound Task.dueAt; `startFrom`/`startTo` bound Task.startDate. A task
  // with a null date on the bounded field never matches an active range on it
  // (an undated task can't fall inside a date window). Absent bound = open-ended
  // on that side.
  dueFrom?: number | null;
  dueTo?: number | null;
  startFrom?: number | null;
  startTo?: number | null;
  // RECURRING facet (boolean, not an id-array): when true, keep ONLY tasks that
  // carry a bound schedule (task.schedule != null — the live per-task recurrence
  // mechanism). Absent/false = no constraint (we do NOT offer a "non-recurring
  // only" mode). Composes (ANDs) with every other facet, so "Recurring + Agents"
  // shows recurring agent tasks.
  recurring?: boolean;
}

export function emptyFilters(): TaskFilters {
  return {
    assigneeIds: [],
    kinds: [],
    priorities: [],
    projects: [],
    labels: [],
    cycles: [],
    modules: [],
    stateGroups: [],
    states: [],
    createdByIds: [],
  };
}

// A PERSISTED filter value's legacy vocabulary → its state group. This is the
// only status→group table left in the UI, and it is deliberately not a task
// reader: it translates a string a PREVIOUS BUILD wrote into localStorage, where
// no task and no wire payload is involved, so there is nothing to read a real
// `stateGroup` off. Same idiom (and same lifetime) as local-prefs.ts's
// migrateGroupBy. It accepts both the old stored `tasks.status` literals and the
// group names themselves, because the retired facet stored both spellings.
const STATE_GROUP_FOR_PERSISTED_STATUS: Record<string, StateGroupKey> = {
  backlog: "backlog",
  todo: "unstarted",
  pending: "unstarted",
  unstarted: "unstarted",
  in_progress: "started",
  started: "started",
  // The retired "Review" column folded into In Progress when the board moved to
  // the five real groups; a saved view still naming it must land there too.
  pending_review: "started",
  done: "completed",
  completed: "completed",
  cancelled: "cancelled",
};

// A persisted/staged TaskFilters blob (e.g. a saved-view hand-off,
// local-prefs.ts's consumeAppliedFilters) may still carry the retired
// `statuses` facet — the legacy 4-column vocab, removed from TaskFilters. Maps
// each legacy value to its real state group and merges into `stateGroups`,
// dropping `statuses`, so an old saved view's constraint (e.g. statuses:["done"])
// is preserved instead of silently showing everything. An unrecognized value is
// DROPPED rather than defaulted: silently turning it into "unstarted" would
// narrow a saved view to a lane the user never chose.
export function migrateStatusFilters(
  parsed: Partial<TaskFilters> & { statuses?: string[] },
): Partial<TaskFilters> {
  const { statuses, ...rest } = parsed;
  if (!statuses || statuses.length === 0) return rest;
  const migrated = statuses
    .map((s) => STATE_GROUP_FOR_PERSISTED_STATUS[s.toLowerCase()])
    .filter((g): g is StateGroupKey => g !== undefined);
  return { ...rest, stateGroups: [...new Set([...(rest.stateGroups ?? []), ...migrated])] };
}

// A saved view's `filters` is opaque JSON the server round-trips. Throws on a
// blob that is not a filter object, so the caller can report it instead of
// silently showing every work item under the view's name.
export function parseSavedFilters(raw: string): TaskFilters {
  const parsed: unknown = JSON.parse(raw);
  if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new TypeError("saved view filters are not an object");
  }
  return {
    ...emptyFilters(),
    ...migrateStatusFilters(parsed as Partial<TaskFilters> & { statuses?: string[] }),
  };
}

// True when a date-range facet has at least one bound set (either end). A range
// with both ends null/undefined is "no constraint".
function hasDateRange(from?: number | null, to?: number | null): boolean {
  return (from ?? null) !== null || (to ?? null) !== null;
}

// True when no filter is active (the "Overall" default state). Reads the
// optional facets defensively so a filter object that predates them still
// resolves to Overall when the original four are empty.
export function isOverall(f: TaskFilters): boolean {
  // "Overall" is exactly the empty selection — no active facet of any kind.
  // Delegating to activeFilterCount keeps the two in lockstep (a new facet added
  // to the count is automatically reflected here).
  return activeFilterCount(f) === 0;
}

// Total number of distinct active filter selections — drives the count badge on
// the Filters trigger. Optional facets are `?? 0` so a 3-facet-only object never
// throws and never inflates the count.
export function activeFilterCount(f: TaskFilters): number {
  return (
    f.assigneeIds.length +
    f.kinds.length +
    f.priorities.length +
    (f.projects?.length ?? 0) +
    (f.labels?.length ?? 0) +
    (f.cycles?.length ?? 0) +
    (f.modules?.length ?? 0) +
    (f.stateGroups?.length ?? 0) +
    (f.states?.length ?? 0) +
    (f.createdByIds?.length ?? 0) +
    (hasDateRange(f.dueFrom, f.dueTo) ? 1 : 0) +
    (hasDateRange(f.startFrom, f.startTo) ? 1 : 0) +
    (f.recurring ? 1 : 0)
  );
}

// Does a single task survive the active filters? AND across facets, OR within a
// facet. "Overall" (all-empty) passes everything. The optional facets are read
// defensively (absent = no selection) and labels match if ANY of the task's
// labels is in the selected set.
// Each facet-group is its own small predicate (AND-composed by matchesFilters), so
// no single function accumulates the whole filter matrix. Every predicate returns
// true when the facet is inactive (empty selection) — "Overall" passes everything.

// PRIORITY + specific workflow STATE ids.
function matchesStatePicks(task: Task, f: TaskFilters): boolean {
  if (f.priorities.length > 0 && !f.priorities.includes(priorityForTask(task))) return false;
  const stateIds = f.states ?? [];
  // A task with no stateId can never match a specific-state selection.
  if (stateIds.length > 0 && (!task.stateId || !stateIds.includes(task.stateId))) return false;
  return true;
}

// PROJECT / CYCLE (single id) + MODULE / LABEL (many-per-task, match ANY carried).
function matchesAssociations(task: Task, f: TaskFilters): boolean {
  const projects = f.projects ?? [];
  if (projects.length > 0) {
    const p = projectOf(task);
    if (!p || !projects.includes(p)) return false;
  }
  const cycles = f.cycles ?? [];
  if (cycles.length > 0) {
    const c = cycleOf(task);
    if (!c || !cycles.includes(c)) return false;
  }
  const modules = f.modules ?? [];
  if (modules.length > 0 && !modulesOf(task).some((m) => modules.includes(m))) return false;
  const labelFilter = f.labels ?? [];
  if (labelFilter.length > 0 && !labelsOf(task).some((l) => labelFilter.includes(l))) return false;
  return true;
}

// STATE GROUP (real phase via the catalog when supplied; status-derived fallback).
function matchesStateGroup(task: Task, f: TaskFilters, states: StateLite[]): boolean {
  const stateGroups = f.stateGroups ?? [];
  if (stateGroups.length === 0) return true;
  const phaseIndex = states.length > 0 ? statePhaseIndex(states) : undefined;
  return stateGroups.includes(stateGroupOf(task, phaseIndex));
}

// One inclusive date window: a null date on the bounded field never falls inside.
function inDateRange(value: number | null, from?: number | null, to?: number | null): boolean {
  if (value === null) return false;
  if ((from ?? null) !== null && value < from!) return false;
  if ((to ?? null) !== null && value > to!) return false;
  return true;
}

// DUE-DATE + START-DATE ranges.
function matchesDateRanges(task: Task, f: TaskFilters): boolean {
  if (hasDateRange(f.dueFrom, f.dueTo) && !inDateRange(task.dueAt, f.dueFrom, f.dueTo))
    return false;
  if (
    hasDateRange(f.startFrom, f.startTo) &&
    !inDateRange(task.startDate ?? null, f.startFrom, f.startTo)
  )
    return false;
  return true;
}

// CREATED-BY identity + ASSIGNEE kind/identity (both resolved through the pools).
function matchesPeople(task: Task, f: TaskFilters, pools: AssigneePools): boolean {
  const createdByIds = f.createdByIds ?? [];
  if (createdByIds.length > 0) {
    const creator = resolveAssignee(task.createdBy, pools);
    if (!creator || !createdByIds.includes(creator.id)) return false;
  }
  if (f.kinds.length === 0 && f.assigneeIds.length === 0) return true;
  const a = resolveAssignee(task.assigneeId, pools);
  // No assignee can't match a kind or an identity facet.
  if (f.kinds.length > 0 && (!a || !f.kinds.includes(a.kind))) return false;
  if (f.assigneeIds.length > 0 && (!a || !f.assigneeIds.includes(a.id))) return false;
  return true;
}

// `states` (optional) is the project's workflow-state catalog — when supplied, the
// state-GROUP facet resolves each task's REAL phase (task.stateId → state.group)
// instead of the legacy status-derived mapping, so filtering by Backlog/Cancelled
// matches the true phase. Omitting it keeps the status-derived fallback. AND across
// facet-groups (each helper), OR within a facet.
export function matchesFilters(
  task: Task,
  f: TaskFilters,
  pools: AssigneePools,
  states: StateLite[] = [],
): boolean {
  return (
    matchesStatePicks(task, f) &&
    matchesAssociations(task, f) &&
    matchesStateGroup(task, f, states) &&
    matchesDateRanges(task, f) &&
    (!f.recurring || isRecurring(task)) &&
    matchesPeople(task, f, pools)
  );
}

export function filterTasks(
  tasks: Task[],
  f: TaskFilters,
  pools: AssigneePools,
  states: StateLite[] = [],
): Task[] {
  if (isOverall(f)) return tasks;
  return tasks.filter((t) => matchesFilters(t, f, pools, states));
}

// A rendered group: a stable key, a human label, optional metadata for the
// header (priority bucket / resolved assignee kind), and the tasks in it.
export interface TaskGroup {
  key: string;
  label: string;
  // For assignee AND created-by groups: the identity id (or null for the
  // Unassigned / unknown-creator group) so the header can render an
  // AssigneeAvatar. Absent for status/priority groups.
  assigneeId?: string | null;
  kind?: AssigneeKind | null;
  // For status groups: the column key (board uses it as the drop target).
  columnKey?: ColumnKey;
  // For DATA-DRIVEN state groups (group by the project's own task_states): the
  // workflow state id (the drop target written back as `stateId`), the editable
  // color the header swatch/icon tints with, and the canonical Plane phase the
  // StateGroupIcon glyph keys off. Absent for the legacy fixed-column status
  // grouping and every other facet.
  stateId?: string;
  stateColor?: string;
  statePhase?: TaskState["group"];
  // For priority groups: the priority bucket.
  priority?: Priority;
  // For project / label / cycle / module groups: the association id (or null for
  // the "No project / Unlabeled / …" catch-all group).
  associationId?: string | null;
  // For state-group groups: the Plane state-group key.
  stateGroup?: StateGroupKey;
  tasks: Task[];
  // SECOND-LEVEL grouping (Plane's sub-group / kanban swimlanes). Populated ONLY by
  // subGroupTasks: each primary group's `tasks` are re-grouped by the sub-group
  // facet into these child groups (the board renders them as swimlanes, the list as
  // nested sections). Absent for a normal single-level grouping.
  subGroups?: TaskGroup[];
}

// Group tasks by the project's OWN workflow states (data-driven columns) — the
// Plane-faithful board grouping. One group per state in the supplied order
// (the relay returns states by `sequence`), ALWAYS returning every state column
// (empty included) so the board renders the full workflow even before any task
// lands in a column. A task buckets by `stateId`; a task whose stateId is unset
// or references a state not in this list falls back to the FIRST state whose
// canonical phase matches its legacy `status` bucket (so legacy rows that predate
// stateId still land in a sensible column), and otherwise into the first state.
// Each group carries the state's id (drop target), editable color, and phase so
// the header renders the real swatch/icon + the board writes the right stateId.
// `showEmpty` (default true) keeps every state column even with no cards; false
// drops the empty ones (the "hide empty groups" Display toggle).
export function groupByState(tasks: Task[], states: StateLite[], showEmpty = true): TaskGroup[] {
  const groups: TaskGroup[] = states.map((s) => ({
    key: s.id,
    label: s.name,
    stateId: s.id,
    stateColor: s.color,
    statePhase: s.group,
    tasks: [] as Task[],
  }));
  const byId = new Map(groups.map((g) => [g.key, g]));
  // First state matching a phase, for legacy (stateId-less) fallback bucketing.
  const firstByPhase = new Map<TaskState["group"], TaskGroup>();
  for (const g of groups) {
    if (g.statePhase && !firstByPhase.has(g.statePhase)) firstByPhase.set(g.statePhase, g);
  }
  // A task whose stateId is unset or points outside THIS catalog still carries
  // its real phase on the wire (stateGroupOfTask, board.ts), so it falls into the
  // first state of that same phase rather than into an arbitrary column.
  const fallback = groups[0];
  for (const task of tasks) {
    const direct = task.stateId ? byId.get(task.stateId) : undefined;
    if (direct) {
      direct.tasks.push(task);
      continue;
    }
    (firstByPhase.get(stateGroupOfTask(task)) ?? fallback)?.tasks.push(task);
  }
  return showEmpty ? groups : groups.filter((g) => g.tasks.length > 0);
}

// Group tasks by PRIORITY (urgent→low), then a "No priority" group LAST. By
// default only non-empty buckets are returned (a board with no urgent tasks shows
// no urgent column); `showEmpty` (default false) keeps all five buckets when the
// "show empty groups" Display toggle is on.
export function groupByPriority(tasks: Task[], showEmpty = false): TaskGroup[] {
  const order: Priority[] = [...PRIORITY_ORDER, "none"];
  const labels: Record<Priority, string> = {
    urgent: "Urgent",
    high: "High",
    medium: "Medium",
    low: "Low",
    none: "No priority",
  };
  const byPriority = new Map<Priority, Task[]>();
  for (const p of order) byPriority.set(p, []);
  for (const task of tasks) byPriority.get(priorityForTask(task))!.push(task);
  const groups = order.map((p) => ({
    key: p,
    label: labels[p],
    priority: p,
    tasks: byPriority.get(p)!,
  }));
  return showEmpty ? groups : groups.filter((g) => g.tasks.length > 0);
}

// Group tasks by ASSIGNEE — the "per user / per agent" view. One group per
// resolved identity (members, cybos, agents, unknowns), with the UNASSIGNED
// group always LAST. Identity groups are ordered by first appearance in the
// input so the layout is stable and deterministic.
export function groupByAssignee(tasks: Task[], pools: AssigneePools): TaskGroup[] {
  const identity = new Map<string, TaskGroup>();
  const unassigned: Task[] = [];
  for (const task of tasks) {
    const a = resolveAssignee(task.assigneeId, pools);
    if (!a) {
      unassigned.push(task);
      continue;
    }
    let g = identity.get(a.id);
    if (!g) {
      g = { key: a.id, label: a.name, assigneeId: a.id, kind: a.kind, tasks: [] };
      identity.set(a.id, g);
    }
    g.tasks.push(task);
  }
  const groups = [...identity.values()];
  if (unassigned.length > 0) {
    groups.push({
      key: "__unassigned__",
      label: "Unassigned",
      assigneeId: null,
      kind: null,
      tasks: unassigned,
    });
  }
  return groups;
}

// Group tasks by CREATOR (task.createdBy) — Plane's "Created by" grouping. One
// group per resolved creator identity (members / cybos / agents / unknowns),
// ordered by first appearance for a stable layout. Unlike the assignee grouping
// there is no "Unassigned" bucket — every task has a creator — but a createdBy
// that resolves to no pool identity still groups under its own "unknown" identity
// (a short-id label) rather than being dropped. The header reuses assigneeId/kind
// so it can render the creator's AssigneeAvatar exactly like the assignee groups.
export function groupByCreatedBy(tasks: Task[], pools: AssigneePools): TaskGroup[] {
  const identity = new Map<string, TaskGroup>();
  for (const task of tasks) {
    const c = resolveAssignee(task.createdBy, pools);
    // createdBy is always a non-null string, so resolveAssignee only returns null
    // for an empty id; bucket those under a stable "unknown creator" group.
    const id = c?.id ?? "__unknown__";
    let g = identity.get(id);
    if (!g) {
      g = {
        key: id,
        label: c?.name ?? "Unknown",
        assigneeId: c?.id ?? null,
        kind: c?.kind ?? null,
        tasks: [],
      };
      identity.set(id, g);
    }
    g.tasks.push(task);
  }
  return [...identity.values()];
}

// "No grouping" — Plane's ungrouped list. Returns a SINGLE group holding every
// task in input order, so the board/list renders one flat section. Empty input
// yields an empty array (nothing to render), matching the other groupers.
export function groupByNone(tasks: Task[]): TaskGroup[] {
  if (tasks.length === 0) return [];
  return [{ key: "__all__", label: "All work items", tasks: [...tasks] }];
}

// Group tasks by STATE GROUP (Plane's five-group vocabulary). When a `states`
// catalog is supplied, each task's phase is resolved from its REAL workflow state
// (task.stateId → state.group), so all five phases — backlog + cancelled included
// — are reachable and a Cancelled task lands in Cancelled (not folded into To Do).
// Without a catalog it falls back to the legacy status-derived mapping. Returns
// the groups in canonical order (backlog → cancelled). By default empty groups are
// dropped; `showEmpty` (default false) keeps all five phases when the "show empty
// groups" Display toggle is on. `preserveEmptyCompleted` keeps only the completed
// phase when its cards were removed by the Done time window, leaving the reveal
// control reachable without restoring every other empty group.
export function groupByStateGroup(
  tasks: Task[],
  states: StateLite[] = [],
  showEmpty = false,
  preserveEmptyCompleted = false,
): TaskGroup[] {
  const phaseIndex = states.length > 0 ? statePhaseIndex(states) : undefined;
  const byGroup = new Map<StateGroupKey, Task[]>();
  for (const g of STATE_GROUPS) byGroup.set(g.key, []);
  for (const task of tasks) byGroup.get(stateGroupOf(task, phaseIndex))!.push(task);
  const groups = STATE_GROUPS.map((g) => ({
    key: g.key,
    label: g.label,
    stateGroup: g.key,
    tasks: byGroup.get(g.key)!,
  }));
  return showEmpty
    ? groups
    : groups.filter(
        (grp) => grp.tasks.length > 0 || (preserveEmptyCompleted && grp.stateGroup === "completed"),
      );
}

// Generic "group by an optional single-id association" (project / cycle /
// module). Buckets by the resolved id, keeping the "None" catch-all (tasks with
// no such association) LAST. Groups are ordered by first appearance so the
// layout is stable + deterministic, mirroring groupByAssignee. `label` resolves
// the id to a display name via `labelFor` (the caller supplies the lookup); the
// catch-all uses `noneLabel`.
function groupBySingleAssociation(
  tasks: Task[],
  idOf: (t: Task) => string | null,
  noneLabel: string,
  labelFor: (id: string) => string,
): TaskGroup[] {
  const byId = new Map<string, TaskGroup>();
  const none: Task[] = [];
  for (const task of tasks) {
    const id = idOf(task);
    if (!id) {
      none.push(task);
      continue;
    }
    let g = byId.get(id);
    if (!g) {
      g = { key: id, label: labelFor(id), associationId: id, tasks: [] };
      byId.set(id, g);
    }
    g.tasks.push(task);
  }
  const groups = [...byId.values()];
  if (none.length > 0) {
    groups.push({ key: "__none__", label: noneLabel, associationId: null, tasks: none });
  }
  return groups;
}

// The header a group falls back to when its association id resolves to NO name:
// the catalog hasn't loaded yet, the row was deleted, or the viewer can't see it.
// NEVER the raw id — a `tp_…`/uuid in a group header is unreadable to a human and
// is the house rule this repo keeps re-breaking — and never blank, which reads as
// a rendering bug. Same "say WHAT it is, not WHICH one" shape assignee.ts uses for
// an identity it can't resolve ("Removed agent"). The group still keys on the real
// id, so two unresolved projects stay two groups (and stay drop targets); only the
// human-facing label degrades. The strings live in unknown-label.ts.

// Group by PROJECT (forward-compatible projectId). "No project" group LAST.
// `labelFor` resolves the project id to a human name; absent (no catalog) the
// header degrades to UNKNOWN_PROJECT_LABEL, never the raw projectId.
export function groupByProject(
  tasks: Task[],
  labelFor: (id: string) => string = () => UNKNOWN_PROJECT_LABEL,
): TaskGroup[] {
  return groupBySingleAssociation(tasks, projectOf, "No department", labelFor);
}

// Group by CYCLE (forward-compatible cycleId). "No cycle" group LAST. `labelFor`
// resolves the cycle id to a name; absent it degrades to UNKNOWN_CYCLE_LABEL.
export function groupByCycle(
  tasks: Task[],
  labelFor: (id: string) => string = () => UNKNOWN_CYCLE_LABEL,
): TaskGroup[] {
  return groupBySingleAssociation(tasks, cycleOf, "No cycle", labelFor);
}

// Generic "group by a MANY-per-task id association" (labels, modules). A task
// that carries several ids appears in EVERY matching group (Plane behavior — a
// multi-labelled / multi-module issue shows under each). Tasks with no such
// association collect in the `noneLabel` catch-all group LAST. Groups are ordered
// by first appearance for a stable, deterministic layout, mirroring groupByLabel.
// The task's ids are DEDUPED per-task so a duplicate id (e.g. the same label id
// listed twice on a row) never pushes the task into the SAME group twice — that
// double-push both inflates the header count and yields a duplicate `{#each}` key
// downstream (the crash the Board hits).
function groupByMultiAssociation(
  tasks: Task[],
  idsOf: (t: Task) => string[],
  noneLabel: string,
  labelFor: (id: string) => string,
): TaskGroup[] {
  const byId = new Map<string, TaskGroup>();
  const none: Task[] = [];
  for (const task of tasks) {
    const ids = idsOf(task);
    if (ids.length === 0) {
      none.push(task);
      continue;
    }
    const seen = new Set<string>();
    for (const id of ids) {
      if (seen.has(id)) continue; // dedupe: a repeated id must not double-add to its group
      seen.add(id);
      let g = byId.get(id);
      if (!g) {
        g = { key: id, label: labelFor(id), associationId: id, tasks: [] };
        byId.set(id, g);
      }
      g.tasks.push(task);
    }
  }
  const groups = [...byId.values()];
  if (none.length > 0) {
    groups.push({ key: "__none__", label: noneLabel, associationId: null, tasks: none });
  }
  return groups;
}

// Group by MODULE. Modules are MANY-per-task (`moduleIds`) — a task belonging to
// several modules shows under EACH of them (Plane behavior), exactly like labels.
// Tasks in no module collect in "No module" LAST. `labelFor` resolves a module id
// to a name; absent it degrades to UNKNOWN_MODULE_LABEL, never the raw id.
export function groupByModule(
  tasks: Task[],
  labelFor: (id: string) => string = () => UNKNOWN_MODULE_LABEL,
): TaskGroup[] {
  return groupByMultiAssociation(tasks, modulesOf, "No module", labelFor);
}

// Group by LABEL. A task with MANY labels appears in EVERY matching group (Plane
// behavior — a multi-labelled issue shows under each of its labels). Tasks with
// no labels collect in the "No label" group LAST.
// `labelFor` resolves a label id to its human name; absent (no catalog) it
// degrades to UNKNOWN_LABEL_LABEL, never the raw id.
export function groupByLabel(
  tasks: Task[],
  labelFor: (id: string) => string = () => UNKNOWN_LABEL_LABEL,
): TaskGroup[] {
  return groupByMultiAssociation(tasks, labelsOf, "No label", labelFor);
}

// The id→name catalogs the project/label/cycle/module group-by modes need to
// resolve a group's header to a HUMAN name instead of the raw association id.
// labels/cycles/modules are the PROJECT's catalogs (TasksBoard / TasksList already
// hold these as props); `projects` is the WORKSPACE's chat-project catalog
// (projectsCache, the same list the project switcher reads) — task.projectId is a
// chat project id, so that is the list that names a project group. Any omitted
// catalog (or an id missing from it) degrades to the facet's "Unknown …" header,
// never the raw id.
export interface GroupCatalogs {
  labels?: { id: string; name: string }[];
  cycles?: { id: string; name: string }[];
  modules?: { id: string; name: string }[];
  projects?: { id: string; name: string }[];
}

// Build an id→name resolver over a catalog. An absent/empty catalog (not loaded
// yet, or none exists) and an unknown id (deleted, or not visible to this viewer)
// both resolve to `unknownLabel` — the raw id is never a human-facing label.
function nameResolver(
  catalog: { id: string; name: string }[] | undefined,
  unknownLabel: string,
): (id: string) => string {
  if (!catalog || catalog.length === 0) return () => unknownLabel;
  const byId = new Map(catalog.map((c) => [c.id, c.name]));
  return (id) => byId.get(id) ?? unknownLabel;
}

// Dispatch to the right grouping for the active GROUP BY. `states` is the
// project's workflow states (client.fetchProjectStates), consumed by
// "stateGroup" to resolve each task's REAL phase; a task whose stateId is unset
// or absent from the catalog falls back to the status-derived mapping
// (stateGroupOf), so "stateGroup" always renders the five real groups even for a
// stateless project (see groupByStateGroup). `catalogs` supplies the project/
// label/cycle/module id→name maps so those group headers show human names;
// omitting one degrades that facet's headers to its "Unknown …" placeholder —
// never the raw id.
//
// `showEmptyGroups` is the single "show empty groups" Display toggle threaded into
// EVERY fixed-domain grouper (stateGroup / priority) so they both honor it
// consistently: true keeps every bucket in the fixed domain (empties included),
// false drops the empties. Left UNDEFINED (the default) each grouper keeps its
// own historical default — stateGroup/priority drop empties — so an omitted
// argument preserves the exact prior behavior. `preserveEmptyCompleted` is the
// Done-window escape hatch used by the board when completed cards exist outside
// the active window. The data-derived groupers
// (assignee / createdBy / label / cycle / module / project / none) never
// produce an empty group, so the flag is a no-op for them.
//
// The legacy "status" facet (four fixed columns) is RETIRED — there is no case
// for it here; groupByState still exists as a directly-called helper (the mobile
// board's own always-state pager), but the toolbar's GROUP BY dispatch never
// reaches it. `default` is defensive-only (a malformed/legacy
// GroupBy value reaching this function at runtime, e.g. from stale external
// input) and resolves to the same real state-group view "stateGroup" gives, never
// back to the retired fixed columns.
export function groupTasks(
  tasks: Task[],
  groupBy: GroupBy,
  pools: AssigneePools,
  states: StateLite[] = [],
  catalogs: GroupCatalogs = {},
  showEmptyGroups?: boolean,
  preserveEmptyCompleted = false,
): TaskGroup[] {
  switch (groupBy) {
    case "assignee":
      return groupByAssignee(tasks, pools);
    case "createdBy":
      return groupByCreatedBy(tasks, pools);
    case "none":
      return groupByNone(tasks);
    case "priority":
      return groupByPriority(tasks, showEmptyGroups);
    case "project":
      return groupByProject(tasks, nameResolver(catalogs.projects, UNKNOWN_PROJECT_LABEL));
    case "label":
      return groupByLabel(tasks, nameResolver(catalogs.labels, UNKNOWN_LABEL_LABEL));
    case "cycle":
      return groupByCycle(tasks, nameResolver(catalogs.cycles, UNKNOWN_CYCLE_LABEL));
    case "module":
      return groupByModule(tasks, nameResolver(catalogs.modules, UNKNOWN_MODULE_LABEL));
    case "stateGroup":
    default:
      return groupByStateGroup(tasks, states, showEmptyGroups, preserveEmptyCompleted);
  }
}

// Is a second-level (sub-)grouping active? A sub-group facet only takes effect when
// it is a real facet AND differs from the primary group-by ("none" = off, and
// sub-grouping by the SAME facet as the primary is meaningless — one bucket each).
export function isSubGroupActive(groupBy: GroupBy, subGroupBy: GroupBy): boolean {
  return subGroupBy !== "none" && subGroupBy !== groupBy;
}

// Two-LEVEL grouping (Plane's sub-group / kanban swimlanes): group by the primary
// facet, then re-group each primary group's tasks by the sub-group facet into
// `subGroups`. The primary groups keep their full `tasks` (so a caller that ignores
// `subGroups` renders exactly the single-level layout). `showEmptyGroups` threads
// into BOTH levels for consistent empty-bucket handling. When the sub-group facet
// is inactive (isSubGroupActive === false) this returns the plain single-level
// grouping (no `subGroups`), so callers can always call it and branch on presence.
export function subGroupTasks(
  tasks: Task[],
  groupBy: GroupBy,
  subGroupBy: GroupBy,
  pools: AssigneePools,
  states: StateLite[] = [],
  catalogs: GroupCatalogs = {},
  showEmptyGroups?: boolean,
): TaskGroup[] {
  const primary = groupTasks(tasks, groupBy, pools, states, catalogs, showEmptyGroups);
  if (!isSubGroupActive(groupBy, subGroupBy)) return primary;
  for (const g of primary) {
    g.subGroups = groupTasks(g.tasks, subGroupBy, pools, states, catalogs, showEmptyGroups);
  }
  return primary;
}

// Build the deduped list of assignee identities present across the pools, for
// the Filters > Assignee multi-select. Ordered People → Cybos → Agents so the
// menu mirrors the KIND facet.
export interface AssigneeOption {
  id: string;
  name: string;
  kind: AssigneeKind;
}

export function assigneeOptions(pools: AssigneePools): AssigneeOption[] {
  const out: AssigneeOption[] = [];
  const seen = new Set<string>();
  // Dedup on the RESOLVED identity id, not the raw input id: a cybo's many live
  // sessions each carry a distinct agentId but resolve (fold) to the SAME cyboId,
  // so keying on the resolved id collapses them to ONE option instead of one per
  // session (the "Sprite x7" bug). Cybos are pushed before agents, so a cybo present
  // in BOTH pools keeps its authoritative cybo entry and the folded session is
  // skipped; a session with no cyboId stays its own agent identity + option.
  const push = (id: string) => {
    const a = resolveAssignee(id, pools);
    if (!a || seen.has(a.id)) return;
    seen.add(a.id);
    out.push({ id: a.id, name: a.name, kind: a.kind });
  };
  for (const m of pools.members) push(m.userId);
  for (const c of pools.cybos) push(c.id);
  // OFFER only sessions that carry a cybo identity. A raw provider session has no
  // cyboId, so agentDisplayName falls through to the PROVIDER label and every one
  // of them becomes an option called "Claude" — the harness, which is not a person
  // and cannot own work. CYBORG-703 folded a cybo's N sessions into one option;
  // this drops the ones that were never an identity to begin with.
  //
  // Only the OFFER is filtered. resolveAssignee still names these ids, so a task
  // already assigned to one keeps rendering its assignee instead of silently
  // reading as unassigned.
  for (const a of pools.agents) {
    if (!a.cyboId) continue;
    push(a.agentId);
  }
  return out;
}
